<template>
	<section>
		<loader :dialog="loading"></loader>
		<floating-alert
			:message="alertMessage"
			:show="alertShow"
			:type="alertType"
		></floating-alert>

		<v-card elevation="2" rounded="xl">
			<!-- Header -->
			<div class="pl-header">
				<div class="pl-header-left">
					<div class="pl-icon-box">
						<v-icon size="18" color="white">mdi-dumbbell</v-icon>
					</div>
					<div>
						<p class="pl-title">Liste des protocoles</p>
						<p class="pl-subtitle">Gestion des protocoles sportifs</p>
					</div>
				</div>
				<v-text-field
					v-model="search"
					prepend-inner-icon="mdi-magnify"
					placeholder="Rechercher..."
					outlined
					dense
					hide-details
					class="pl-search"
					color="blue darken-2"
				></v-text-field>

				<div class="d-flex align-center gap-2 ml-3" style="gap: 10px;">
					<!-- Create/edit dialog -->
					<v-dialog v-model="dialog" max-width="560px" scrollable>
						<template v-slot:activator="{ on, attrs }">
							<v-btn depressed rounded class="pl-add-btn" v-bind="attrs" v-on="on">
								<v-icon left small>mdi-plus</v-icon>
								Nouveau
							</v-btn>
						</template>
						<v-card rounded="xl" class="pa-2">
							<div class="pl-dialog-header">
								<div class="pl-icon-box-sm">
									<v-icon size="16" color="white">mdi-dumbbell</v-icon>
								</div>
								<div>
									<p class="pl-dialog-title">{{ formTitle }}</p>
									<p class="pl-dialog-sub">Protocole sportif</p>
								</div>
								<v-spacer></v-spacer>
								<v-btn icon small @click="close">
									<v-icon color="#6b7280">mdi-close</v-icon>
								</v-btn>
							</div>
							<v-divider></v-divider>
							<v-card-text class="pt-5">
								<v-row>
									<v-col cols="12">
										<v-text-field
											v-model="editedItem.label"
											label="Intitulé du protocole"
											outlined
											dense
											rounded
											prepend-inner-icon="mdi-dumbbell"
											hide-details
											class="mb-3"
										></v-text-field>
									</v-col>
									<v-col cols="12">
										<v-text-field
											v-model="editedItem.duration"
											label="Durée"
											outlined
											dense
											rounded
											prepend-inner-icon="mdi-timer"
											hide-details
											class="mb-3"
										></v-text-field>
									</v-col>
									<v-col cols="12">
										<label class="field-label">Description</label>
										<VueEditor
											v-model="editedItem.description"
											:editorToolbar="customToolbar"
											class="mt-2"
										></VueEditor>
									</v-col>
								</v-row>
							</v-card-text>
							<v-card-actions class="pa-4 pt-2">
								<v-btn text color="error" rounded @click="close">Annuler</v-btn>
								<v-spacer></v-spacer>
								<v-btn depressed rounded class="pl-save-btn" @click="save">
									Enregistrer
								</v-btn>
							</v-card-actions>
						</v-card>
					</v-dialog>
				</div>
			</div>

			<v-divider></v-divider>

			<!-- Table -->
			<v-data-table
				:headers="headers"
				:items="protocols"
				:single-expand="true"
				:expanded.sync="expanded"
				item-key="_id"
				show-expand
				class="pl-table"
				:loading="loadingTable"
				loading-text="Chargement en cours..."
				:server-items-length="totalItems"
				:options.sync="options"
				:footer-props="{
					'items-per-page-options': [10, 25, 50],
					'items-per-page-all-text': 'Tout',
					'items-per-page-text': 'Lignes par page',
				}"
			>
				<!-- Exercises expanded row -->
				<template v-slot:expanded-item="{ headers }">
					<td :colspan="headers.length" class="pa-0">
						<div class="expanded-exercises pa-4">
							<p class="exercises-label">
								<v-icon size="16" color="#c62828" class="mr-1">mdi-weight-lifter</v-icon>
								Exercices associés
							</p>
							<div v-if="exercises.length === 0" class="text-center py-4 text-caption grey--text">
								Aucun exercice associé
							</div>
							<div
								v-for="ex in exercises"
								:key="ex._id"
								class="exercise-row"
							>
								<div class="exercise-icon-box">
									<v-icon size="15" color="white">mdi-weight-lifter</v-icon>
								</div>
								<div class="exercise-info">
									<p class="exercise-name">{{ ex.exercice.label }}</p>
									<p class="exercise-date">Ajouté le {{ ex.createdAt }}</p>
								</div>
								<div class="ml-auto d-flex" style="gap: 6px;">
									<v-btn
										small
										rounded
										outlined
										color="primary"
										@click="editedItem = ex.exercice; dialogExercise = true;"
									>
										<v-icon left x-small>mdi-eye</v-icon>
										Voir
									</v-btn>
									<v-btn
										small
										rounded
										outlined
										color="error"
										@click="deleteItem(ex)"
									>
										<v-icon left x-small>mdi-close-circle</v-icon>
										Retirer
									</v-btn>
								</div>
							</div>
						</div>
					</td>
				</template>

				<template v-slot:[`item.actions`]="{ item }">
					<v-btn icon small @click="editItem(item)">
						<v-icon size="17" color="#374151">mdi-pencil</v-icon>
					</v-btn>
					<v-btn icon small @click="deleteItem(item)">
						<v-icon size="17" color="#c62828">mdi-delete</v-icon>
					</v-btn>
				</template>

				<template v-slot:[`footer.page-text`]="items">
					{{ items.pageStart }}-{{ items.pageStop }} sur {{ items.itemsLength }}
				</template>
				<template v-slot:no-data>
					<v-btn text color="primary" @click="initialize">Actualiser</v-btn>
				</template>
			</v-data-table>
		</v-card>

		<!-- Client attribution (single) dialog -->
		<v-dialog v-model="dialogUsers" max-width="500px" scrollable>
			<client-list
				:protocolId="protocolId"
				@close-client-dialog="dialogUsers = false"
				@successful-attribution="
					manageAlert(true, 'success', 'Attribution effectuée');
					dialogUsers = false;
				"
			></client-list>
		</v-dialog>

		<!-- Exercise details dialog -->
		<v-dialog v-model="dialogExercise" max-width="560px" scrollable>
			<v-card rounded="xl" class="pa-2">
				<div class="ex-info-header">
					<div class="ex-info-icon">
						<v-icon size="16" color="white">mdi-weight-lifter</v-icon>
					</div>
					<div>
						<p class="ex-info-title">Détails de l'exercice</p>
						<p class="ex-info-sub">{{ editedItem.label }}</p>
					</div>
					<v-spacer></v-spacer>
					<v-btn icon small @click="dialogExercise = false; editedItem = defaultItem;">
						<v-icon color="#6b7280">mdi-close</v-icon>
					</v-btn>
				</div>
				<v-divider></v-divider>
				<v-card-text class="pt-4">
					<v-row>
						<v-col cols="12" sm="6">
							<v-text-field disabled v-model="editedItem.label" label="Nom" outlined dense rounded hide-details></v-text-field>
						</v-col>
						<v-col cols="12" sm="6">
							<v-text-field v-model="editedItem.videoLink" label="Lien vidéo" outlined dense rounded hide-details disabled></v-text-field>
						</v-col>
						<v-col cols="12" sm="6">
							<label class="field-label">Durée</label>
							<vue-number-input disabled v-model="editedItem.duration" center size="small" controls></vue-number-input>
						</v-col>
						<v-col cols="12" sm="6">
							<label class="field-label">Nombre de séries</label>
							<vue-number-input disabled v-model="editedItem.series" center size="small" controls></vue-number-input>
						</v-col>
						<v-col cols="12" sm="4">
							<label class="field-label">Tps d'exécution</label>
							<vue-number-input disabled v-model="editedItem.executionDuration" center size="small" controls></vue-number-input>
						</v-col>
						<v-col cols="12" sm="4">
							<label class="field-label">Tps de récupération</label>
							<vue-number-input disabled v-model="editedItem.recoveryDuration" center size="small" controls></vue-number-input>
						</v-col>
						<v-col cols="12" sm="4">
							<label class="field-label">Répétitions</label>
							<vue-number-input disabled v-model="editedItem.repetition" center size="small" controls></vue-number-input>
						</v-col>
						<v-col cols="12">
							<label class="field-label">Description</label>
							<VueEditor disabled v-model="editedItem.description" :editorToolbar="customToolbar" class="mt-2"></VueEditor>
						</v-col>
					</v-row>
				</v-card-text>
				<v-card-actions class="pa-4 pt-2">
					<v-spacer></v-spacer>
					<v-btn text color="error" rounded @click="dialogExercise = false; editedItem = defaultItem;">Fermer</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Delete dialog -->
		<v-dialog v-model="dialogDelete" max-width="440px">
			<v-card rounded="xl" class="pa-2">
				<div class="del-header">
					<div class="del-icon"><v-icon size="18" color="white">mdi-alert</v-icon></div>
					<p class="del-title">Supprimer cet élément ?</p>
				</div>
				<v-card-actions class="pa-4 pt-2">
					<v-btn text color="grey" rounded @click="closeDelete">Annuler</v-btn>
					<v-spacer></v-spacer>
					<v-btn depressed rounded class="del-confirm-btn" @click="deleteItemConfirm">Supprimer</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</section>
</template>

<script>
import service from "@/services/index";
import Loader from "@/components/Loader.vue";
import FloatingAlert from "@/components/FloatingAlert.vue";
import ClientList from "@/components/Admin/Protocols/ClientList.vue";

export default {
	components: { Loader, FloatingAlert, ClientList },
	data: () => ({
		search: "",
		searchTimeout: null,
		expanded: [],
		loadingTable: false,
		dialogUsers: false,
		dialogExercise: false,
		loading: false,
		dialog: false,
		dialogDelete: false,
		options: {},
		totalItems: 0,
		headers: [
			{ text: "Intitulé", align: "start", value: "label" },
			{ text: "Actions", value: "actions", sortable: false },
			{ text: "", value: "data-table-expand" },
		],
		protocols: [],
		exercises: [],
		protocolId: "",
		editedIndex: -1,
		exerciseAndProtocolIds: { exerciseId: "", protocolId: "" },
		defaultExerciseAndProtocolIds: { exerciseId: "", protocolId: "" },
		editedItem: { label: "", duration: 0, description: "" },
		defaultItem: { label: "", duration: 0, description: "" },
		customToolbar: [
			[{ header: [false, 1, 2, 3, 4, 5, 6] }],
			["bold", "italic", "underline"],
			[{ color: [] }],
			[{ list: "ordered" }, { list: "bullet" }],
		],
		alertMessage: "",
		alertShow: null,
		alertType: "",
	}),
	computed: {
		formTitle() {
			return this.editedIndex === -1 ? "Nouveau protocole" : "Modifier un protocole";
		},
	},
	watch: {
		options: {
			handler() { this.initialize(); },
			deep: true,
		},
		search() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				if (this.options.page !== 1) {
					this.options.page = 1;
				} else {
					this.initialize();
				}
			}, 400);
		},
		dialog(val) { val || this.close(); },
		dialogDelete(val) { val || this.closeDelete(); },
		expanded(protocol) {
			if (protocol.length > 0) this.getProtocolExercises(protocol[0]._id);
		},
	},
	methods: {
		initialize() {
			this.protocols = [];
			this.loadingTable = true;
			const page = this.options.page || 1;
			const limit = this.options.itemsPerPage || 10;
			service.getProtocolsPaginated({ page, limit, search: this.search }).then((response) => { this.protocols = response.data.data.protocols; this.totalItems = response.data.data.totalPages * limit; this.loadingTable = false; }).catch((e) => { console.log(e); this.loadingTable = false; });
		},
		manageAlert(display, type, message) {
			this.alertShow = display;
			this.alertType = type;
			this.alertMessage = message;
		},
		showClients(item) { this.protocolId = item._id; this.dialogUsers = true; },
		editItem(item) { this.editedIndex = this.protocols.indexOf(item); this.editedItem = Object.assign({}, item); this.dialog = true; },
		deleteItem(item) {
			if (item.exercice) {
				this.exerciseAndProtocolIds.exerciseId = item.exercice._id;
				this.exerciseAndProtocolIds.protocolId = item.protocol;
			} else {
				this.editedIndex = this.protocols.indexOf(item);
				this.editedItem = Object.assign({}, item);
			}
			this.dialogDelete = true;
		},
		deleteItemConfirm() {
			this.loading = true;
			if (this.exerciseAndProtocolIds.exerciseId) {
				service.removeExerciseToProtocol(this.exerciseAndProtocolIds.protocolId, this.exerciseAndProtocolIds.exerciseId).then((r) => { this.getProtocolExercises(this.exerciseAndProtocolIds.protocolId); this.closeDelete(); this.manageAlert(true, "success", r.message); this.loading = false; }).catch((e) => { this.loading = false; this.manageAlert(true, "red", e.message); });
			} else {
				service.deleteProtocol(this.editedItem._id).then((r) => { this.protocols.splice(this.editedIndex, 1); this.closeDelete(); this.manageAlert(true, "success", r.message); this.loading = false; }).catch((e) => { this.loading = false; this.manageAlert(true, "red", e.message); this.closeDelete(); });
			}
		},
		close() { this.dialog = false; this.$nextTick(() => { this.editedItem = Object.assign({}, this.defaultItem); this.editedIndex = -1; }); },
		closeDelete() {
			this.dialogDelete = false;
			this.$nextTick(() => { this.editedItem = Object.assign({}, this.defaultItem); this.exerciseAndProtocolIds = Object.assign({}, this.defaultExerciseAndProtocolIds); this.editedIndex = -1; });
		},
		save() {
			if (this.editedIndex > -1) {
				this.loading = true;
				service.updateProcotol(this.editedItem._id, this.editedItem).then(() => { this.initialize(); this.loading = false; this.close(); this.manageAlert(true, "success", "Modification effectuée"); }).catch((e) => { console.log(e); this.loading = false; this.close(); this.manageAlert(true, "error", "Echec de modification du protocole"); });
			} else {
				this.loading = true;
				service.createProcotol(this.editedItem).then(() => { this.protocols.push(this.editedItem); this.loading = false; this.close(); this.manageAlert(true, "success", "Nouveau protocole créé"); }).catch((e) => { console.log(e); this.manageAlert(true, "error", "Echec de création du protocole"); });
			}
		},
		getProtocolExercises(protocolId) {
			this.loading = true;
			service.getProtocolExercises(protocolId).then((r) => { this.loading = false; this.exercises = r.data?.exercises || r.data || []; }).catch(() => { this.loading = false; });
		},
	},
};
</script>

<style scoped>
.pl-header { display: flex; align-items: center; padding: 18px 20px 14px; gap: 14px; flex-wrap: wrap; }
.pl-header-left { display: flex; align-items: center; gap: 14px; flex: 1; min-width: 200px; }
.pl-icon-box { width: 38px; height: 38px; border-radius: 10px; background: #c62828; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.pl-title { font-size: 0.92rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.pl-subtitle { font-size: 0.72rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.pl-search { max-width: 240px; }
.pl-add-btn { background: #c62828 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }
.pl-table { font-family: Montserrat, sans-serif; }
.pl-dialog-header { display: flex; align-items: center; gap: 14px; padding: 18px 20px 14px; }
.pl-icon-box-sm { width: 34px; height: 34px; border-radius: 8px; background: #c62828; display: flex; align-items: center; justify-content: center; }
.pl-dialog-title { font-size: 0.88rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.pl-dialog-sub { font-size: 0.68rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.pl-save-btn { background: #1565c0 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }
.field-label { font-size: 0.75rem; font-weight: 600; color: #6b7280; font-family: Montserrat, sans-serif; }
.expanded-exercises { background: #fafafa; border-top: 1px solid #f3f4f6; }
.exercises-label { font-size: 0.78rem; font-weight: 700; color: #374151; margin: 0 0 12px; font-family: Montserrat, sans-serif; }
.exercise-row { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid #f3f4f6; }
.exercise-row:last-child { border-bottom: none; }
.exercise-icon-box { width: 30px; height: 30px; border-radius: 8px; background: linear-gradient(135deg, #c62828, #b71c1c); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.exercise-name { font-size: 0.82rem; font-weight: 600; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.exercise-date { font-size: 0.68rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.ex-info-header { display: flex; align-items: center; gap: 14px; padding: 18px 20px 14px; }
.ex-info-icon { width: 34px; height: 34px; border-radius: 8px; background: #c62828; display: flex; align-items: center; justify-content: center; }
.ex-info-title { font-size: 0.88rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.ex-info-sub { font-size: 0.68rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.del-header { display: flex; align-items: center; gap: 12px; padding: 16px 20px 12px; }
.del-icon { width: 34px; height: 34px; border-radius: 8px; background: #c62828; display: flex; align-items: center; justify-content: center; }
.del-title { font-size: 0.9rem; font-weight: 700; color: #111827; margin: 0; font-family: Montserrat, sans-serif; }
.del-confirm-btn { background: #c62828 !important; color: white !important; font-family: Montserrat, sans-serif; font-weight: 700; font-size: 0.82rem; }
</style>

